/**
 * The signed-out screens' shared controls (Claude Design `Login D` / `Login Mobile` / `Register`).
 *
 * Sign-in and registration draw the same pill field and the same dark-green CTA, so they share one
 * definition here rather than each keeping a copy that can drift.
 */

/**
 * A pill field: white, rounded, icon on the left. The design draws the filled field with a green
 * border and a soft green glow and the empty one with a pale border, so `filled` reproduces that —
 * focus gets the same treatment.
 */
export const pillFieldClass = (filled: boolean): string =>
    "flex items-center gap-2.5 rounded-full bg-surface px-5 py-3.5 short:py-2.5 shorter:py-2 border transition " +
    "focus-within:border-leaf focus-within:shadow-[0_4px_16px_rgba(22,163,74,.1)] dark:bg-surface/10 " +
    (filled ? "border-leaf shadow-[0_4px_16px_rgba(22,163,74,.1)]" : "border-leaf/30 dark:border-leaf/25");

/**
 * The input itself is chrome-free — the pill around it carries the border and background.
 *
 * `auth-pill-input` is not a Tailwind utility: it hooks the `:-webkit-autofill` override in
 * index.css, which cancels the pale-blue box Chrome paints behind an autofilled value. That needs
 * four selector states and a UA-beating box-shadow, which is why it is a stylesheet rule.
 */
export const pillInputClass =
    "auth-pill-input min-w-0 flex-1 bg-transparent text-[14.5px] text-ink outline-none placeholder:text-ink-faint";

/**
 * The dark-green CTA pill. Dark mode flips it to the bright leaf green: `bg-sidebar` is #0c2a17
 * there, which is all but invisible against the dark backdrop.
 */
export const pillButtonClass =
    "rounded-full bg-sidebar px-6 py-4 short:py-3 shorter:py-2.5 text-[14.5px] font-bold tracking-[.04em] text-white " +
    "shadow-[0_10px_26px_rgba(20,83,45,.3)] transition hover:bg-sidebar/90 disabled:opacity-60 md:text-[15px] " +
    "dark:bg-leaf dark:text-[#0f140f] dark:shadow-[0_10px_26px_rgba(74,222,128,.2)] dark:hover:bg-leaf/90";
